import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Select,
  Table,
  Descriptions,
} from 'antd';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import CompanySettingsCard from '../CompanySettingsCard';
import { getBoards, getBoardDetails } from '../../boards/state/boards.actions';
import { getSourceFields } from '../../abWorkReporting/ABWorkImportHelpers';
import {
  RECORD_TYPE_OPTIONS,
  WORK_ORDER_TYPES,
  ACCEPT_REJECT_TYPES,
} from '../../abWorkReporting/ABWorkImport.constants';
import WorkflowConfigureSourceSelector from '../../forms/FormWorkflows/selectors/WorkflowConfigureSourceSelector';
import { getIdMap } from '../../helpers/helpers';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';

const validBucketFieldTypes = new Set([
  'text',
  'yes-no',
  'attribute',
  'calculation',
  'dateTime',
]);

const validCardFieldTypes = new Set([
  'text',
  'yes-no',
  'attribute',
  'calculation',
  'dateTime',
  'table - Buckets',
  'dropdown - Buckets',
]);

const buildStaticFileFieldMap = (bucketMappings) => {
  const fieldTypeMap = {
    table: [],
    dropdown: [],
    text: [],
    attribute: [],
  };

  fieldTypeMap.text.push({ id: 'jobCode', title: 'Job Code', recordType: null });
  fieldTypeMap.attribute.push({ id: 'jobCode', title: 'Job Code', recordType: null });

  RECORD_TYPE_OPTIONS.forEach((recordTypeOption) => {
    const recordType = recordTypeOption.id;
    const recordTypeEntry = {
      id: recordType,
      title: recordTypeOption.title,
      subDataType: bucketMappings[recordType],
    };

    fieldTypeMap.table.push(recordTypeEntry);
    fieldTypeMap.dropdown.push(recordTypeEntry);
  });

  Object.entries(WORK_ORDER_TYPES).forEach(([recordId, columns]) => {
    if (ACCEPT_REJECT_TYPES.has(recordId)) return;
    columns?.forEach((column) => {
      column.validTypes?.forEach((validType) => {
        if (!fieldTypeMap[validType]) {
          fieldTypeMap[validType] = [];
        }
        fieldTypeMap[validType].push({
          id: `${recordId}-${column.dataIndex}`,
          title: `Record Type ${recordId} - ${column.title}`,
          recordType: recordId,
          subDataType: bucketMappings[recordId],
        });
      });
    });
  });

  return fieldTypeMap;
};

export default function ABWorkReportingImportMappings({
  mapping,
  onSaveMapping,
  disabled,
}) {
  const dispatch = useDispatch();
  const boards = useSelector((state) => state.boards.boards);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);

  const [bucketMappings, setBucketMappings] = useState({});
  const [fieldMappings, setFieldMappings] = useState({});
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedRevisionTextField, setSelectedRevisionTextField] = useState(null);
  const [prevMapping, setPrevMapping] = useState(null);

  const bucketTemplateMap = useMemo(() => getIdMap(bucketTemplates), [bucketTemplates]);

  useEffect(() => {
    dispatch(getBoards());
  }, []);

  if (prevMapping !== mapping) {
    setPrevMapping(mapping);
    try {
      setBucketMappings(
        typeof mapping?.bucketMappings === 'string'
          ? JSON.parse(mapping.bucketMappings)
          : mapping?.bucketMappings || {},
      );
      setFieldMappings(
        typeof mapping?.fieldMappings === 'string'
          ? JSON.parse(mapping.fieldMappings)
          : mapping?.fieldMappings || {},
      );
    } catch (_err) {
      setBucketMappings({});
      setFieldMappings({});
    }
    setSelectedBoard(mapping?.boardId || null);
    setSelectedStatus(mapping?.statusId || null);
    setSelectedRevisionTextField(mapping?.revisionTextField || null);
  }

  useEffect(() => {
    if (selectedBoard) {
      dispatch(getBoardDetails(selectedBoard, false));
    }
  }, [selectedBoard]);

  const fileFieldMap = useMemo(
    () => buildStaticFileFieldMap(bucketMappings),
    [bucketMappings],
  );

  const boardOptions = useMemo(() => (
    Object.values(boards).map((board) => ({
      label: board.title,
      value: board.id,
    }))
  ), [boards]);

  const statusOptions = boardDetailsMap[selectedBoard]?.statuses?.map((status) => ({
    label: status.title,
    value: status.id,
  }));

  const selectedCardType = cardTemplates[boards[selectedBoard]?.cardTypeId];

  const cardFieldOptions = getSourceFields({
    sourceSections: selectedCardType?.fields,
    validFieldTypes: validCardFieldTypes,
    baseFields: [{
      id: 'cardTitle',
      name: 'Card Title',
      type: 'text',
      isRequired: true,
    }],
  });

  const revisionTextFieldOptions = useMemo(() => (
    cardFieldOptions
      .filter((f) => f.type === 'text')
      .map((f) => ({ label: f.name, value: f.id }))
  ), [cardFieldOptions]);

  const onBucketMappingsChange = (newMappings) => {
    setBucketMappings(newMappings);
  };

  const onFieldMappingsChange = (newMappings) => {
    setFieldMappings(newMappings);
  };

  const onBoardChange = (value) => {
    setSelectedBoard(value);
    setSelectedStatus(null);
    setSelectedRevisionTextField(null);
  };

  const onSave = () => {
    onSaveMapping({
      bucketMappings,
      fieldMappings,
      boardId: selectedBoard,
      statusId: selectedStatus,
      revisionTextField: selectedRevisionTextField,
    });
  };

  const bucketColumns = [
    {
      dataIndex: 'title',
      title: 'Record Type',
    },
    {
      dataIndex: 'bucketTemplateId',
      title: 'Bucket Template',
      render: (_, record) => {
        const selectedBucketTemplates = new Set(
          RECORD_TYPE_OPTIONS.map((rt) => bucketMappings[rt.id]).filter(Boolean),
        );
        const eligible = bucketTemplates.filter((bt) => (
          !selectedBucketTemplates.has(bt.id) || bucketMappings[record.id] === bt.id
        ));

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={bucketMappings}
            value={bucketMappings[record.id]}
            onFieldMappingsChange={onBucketMappingsChange}
            targetId={record.id}
            eligibleSourceFields={eligible}
            disabled={disabled}
          />
        );
      },
    },
  ];

  const getBucketFieldColumns = (bucketTemplateId) => [
    {
      dataIndex: 'name',
      title: 'Bucket Field',
      render: (_, record) => (
        <span>
          {record.name}
          {record.isRequired && <span className="form-required-field" />}
        </span>
      ),
    },
    {
      dataIndex: 'fileField',
      title: 'File Field',
      render: (_, record) => {
        let relevantFileFields = fileFieldMap[record.type] || [];
        let selectedRecordType = null;

        Object.keys(bucketMappings).forEach((key) => {
          if (bucketMappings[key] === bucketTemplateId) {
            selectedRecordType = key;
          }
        });

        if (selectedRecordType && relevantFileFields.length > 0) {
          relevantFileFields = relevantFileFields.filter((ff) => (
            ff.recordType === selectedRecordType
            || !ff.recordType
            || ff.recordType === '15'
          ));
        }

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={bucketMappings}
            value={bucketMappings[record.id]}
            onFieldMappingsChange={onBucketMappingsChange}
            targetId={record.id}
            eligibleSourceFields={relevantFileFields}
            disabled={disabled}
          />
        );
      },
    },
  ];

  const selectedBucketTemplateData = useMemo(() => (
    RECORD_TYPE_OPTIONS
      .map((rt) => {
        const btId = bucketMappings[rt.id];
        const bt = bucketTemplateMap[btId];
        if (!bt) return null;
        const sourceFields = getSourceFields({
          sourceSections: bt.customFields?.sections,
          validFieldTypes: validBucketFieldTypes,
          baseFields: [
            { id: `${btId}-name`, name: 'Name', type: 'text', isRequired: true },
            { id: `${btId}-description`, name: 'Description', type: 'text' },
          ],
        });
        return { ...bt, sourceFields };
      })
      .filter(Boolean)
  ), [bucketMappings, bucketTemplateMap]);

  const cardFieldColumns = [
    {
      dataIndex: 'name',
      title: 'Card Field',
      render: (_, record) => (
        <span>
          {record.name}
          {record.isRequired && <span className="form-required-field" />}
        </span>
      ),
    },
    {
      dataIndex: 'fileField',
      title: 'File Field',
      render: (_, record) => {
        let relevantFileFields = fileFieldMap[record.type] || [];
        if (record.subDataType) {
          relevantFileFields = relevantFileFields.filter((ff) => (
            ff.subDataType === record.subDataType
          ));
        }

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={fieldMappings}
            value={fieldMappings[record.id]}
            onFieldMappingsChange={onFieldMappingsChange}
            targetId={record.id}
            eligibleSourceFields={relevantFileFields}
            disabled={disabled}
          />
        );
      },
    },
  ];

  return (
    <CompanySettingsCard
      title="Work Order Import Mappings"
      style={{ width: '100%' }}
      containerStyle={{ maxHeight: 'none', overflowY: 'visible' }}
    >
      <Row style={{ marginTop: 10, marginBottom: 10 }}>
        <Col span={6}>Board</Col>
        <Col span={18}>
          <Select
            options={boardOptions}
            value={selectedBoard}
            onChange={onBoardChange}
            style={{ width: '100%' }}
            allowClear
            disabled={disabled}
          />
        </Col>
      </Row>
      {selectedBoard && (
        <>
          <Row style={{ marginBottom: 10 }}>
            <Col span={6}>Status</Col>
            <Col span={18}>
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                style={{ width: '100%' }}
                allowClear
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row style={{ marginBottom: 10 }}>
            <Col span={6}>Revision Text Field</Col>
            <Col span={18}>
              <Select
                options={revisionTextFieldOptions}
                value={selectedRevisionTextField}
                onChange={setSelectedRevisionTextField}
                style={{ width: '100%' }}
                allowClear
                disabled={disabled}
              />
            </Col>
          </Row>
        </>
      )}
      <Descriptions title="Bucket Mappings" style={{ marginTop: 10 }} />
      <Table
        dataSource={RECORD_TYPE_OPTIONS}
        columns={bucketColumns}
        pagination={false}
        size="small"
        rowKey="id"
      />
      {selectedBucketTemplateData.map((bt) => (
        <React.Fragment key={bt.id}>
          <Descriptions title={bt.name} style={{ marginTop: 10 }} />
          <Table
            dataSource={bt.sourceFields}
            columns={getBucketFieldColumns(bt.id)}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </React.Fragment>
      ))}
      {selectedBoard && (
        <>
          <Descriptions title="Card Field Mappings" style={{ marginTop: 10 }} />
          <Table
            dataSource={cardFieldOptions}
            columns={cardFieldColumns}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </>
      )}
      {!disabled && (
        <Row justify="end" style={{ marginTop: 15, marginBottom: 10 }}>
          <OnTraccrButton
            type="primary"
            onClick={onSave}
            title="Save Mappings"
          />
        </Row>
      )}
    </CompanySettingsCard>
  );
}

ABWorkReportingImportMappings.propTypes = {
  mapping: PropTypes.object,
  onSaveMapping: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ABWorkReportingImportMappings.defaultProps = {
  mapping: {},
  disabled: false,
};
